import React from 'react';
import { Link as ReactRouterLink } from 'react-router-dom';
import { ContactBox } from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import { getSortFn } from '@fortress-technology-solutions/fortress-component-library/utils';
import useIntl from '../../../hooks/useIntl';
import type { Applicant } from '../../CreateApplication/types';
import { getUrlWithSelectedPropertyId } from '../../../utils/navigation-helpers';
import UnitAccessibilityInfo from '../../../components/UnitAccessibilityInfo';
import messages from './messages';

type Props = {
  applicants: Array<Applicant>,
  isResident: boolean,
  unitId: string,
  unitNumber: string,
  isCommercial: boolean,
  isUnitSoftDeleted?: boolean,
  floorPlanAtMoveOut?: string,
  showFloorPlanAtMoveOut?: boolean,
  floorPlanId?: string,
  floorPlanName?: string,
  showCurrentFloorPlan?: boolean,
  residentId?: string,
  organizationId: string,
  propertyId: string,
  householdId: string,
};

const filterAndTransformApplicants = (
  applicants: Array<Applicant>,
  isCommercial: boolean,
) => {
  const filtered = applicants.filter(
    (applicant) =>
      applicant.resident?.status === 'Current' &&
      applicant.applicantType.financiallyResponsible,
  );

  const sortFn = getSortFn(
    'applicantCustomer.customer.firstName',
    'ASC',
  ).string;
  const sorted = [...filtered].sort(sortFn);

  return sorted.map((applicant) => {
    const customer = applicant.applicantCustomer?.customer ?? {};
    return {
      firstName: customer.firstName ?? '',
      lastName: isCommercial ? '' : customer.lastName ?? '',
      phoneNumber: customer.phoneNumber ?? '',
      emailAddress: customer.emailAddress ?? '',
    };
  });
};

const getVariant = (isResident, isCommercial) => {
  if (isResident) {
    return isCommercial ? 'currentTenant' : 'currentResident';
  } else {
    return isCommercial ? 'priorTenant' : 'priorResident';
  }
};

const ResidentProfileDetails = ({
  applicants,
  isResident,
  unitNumber,
  unitId,
  isCommercial,
  isUnitSoftDeleted = false,
  floorPlanAtMoveOut,
  showFloorPlanAtMoveOut = false,
  floorPlanId,
  floorPlanName,
  showCurrentFloorPlan = false,
  residentId,
  organizationId,
  propertyId,
  householdId,
}: Props) => {
  const intl = useIntl();
  const contacts = filterAndTransformApplicants(applicants, isCommercial);

  const currentResidentFloorPlanSubtext =
    isResident && showCurrentFloorPlan && floorPlanId ? (
      <ReactRouterLink
        to={{
          pathname: getUrlWithSelectedPropertyId(`/floorplan/${floorPlanId}`),
          search: `?prev=resident&residentId=${residentId ?? ''}`,
          state: { canGoBack: true },
        }}
      >
        {floorPlanName || intl.formatMessage(messages.floorPlan)}
      </ReactRouterLink>
    ) : undefined;

  const unitSubtext =
    currentResidentFloorPlanSubtext ??
    (!isResident && showFloorPlanAtMoveOut ? floorPlanAtMoveOut : undefined);

  return (
    <ContactBox
      ReactRouterLink={ReactRouterLink}
      contacts={contacts}
      intl={intl}
      isLoading={false}
      variant={getVariant(isResident, isCommercial)}
      unitToURL={getUrlWithSelectedPropertyId(`/unit/${unitId}`)}
      unitLinkDisabled={isUnitSoftDeleted}
      unitId={unitId}
      unitNumber={unitNumber}
      unitSubtext={unitSubtext}
      // On the unit number's own row, which is where the design puts it and
      // what the story asks for: the indicator reads as part of the unit
      // rather than as a block below the contacts. Prior residents are left
      // out of the MVP — this screen serves them too, and the value would have
      // to be the one in force while they lived there.
      unitAdornment={
        <UnitAccessibilityInfo
          organizationId={organizationId}
          propertyId={propertyId}
          householdId={householdId}
          enabled={isResident}
          inline
        />
      }
      applicantTypeOptions={[]}
      onSelectChange={() => {}}
    />
  );
};

export default ResidentProfileDetails;
